/**
 * Theater (W2-02; DESIGN.md §4.9, ARCHITECTURE.md §7.5; `\`): the preview canvas re-parented into a full-window
 * overlay (same GL context, nothing re-uploaded) showing the Clean render of normal editor playback.
 *  - Keys: every chord the active keymap binds to a Playback command still reaches its binding (Space; the RPE
 *    preset's I / O / P / [ play maximised, stop and return, stop in place, replay; T / U hold-to-play; rate keys),
 *    plus seeking, Theater and test play (`THEATER_KEYS`); editing keys do nothing while the chart plays
 *    full-window (UX-050).
 *  - Esc stops playback and returns the playhead to where it started (RPE NAV-07, the I then Esc habit), scrolls the
 *    lane and motion columns back there too (UX-055), then leaves Theater; `\` leaves Theater and keeps playing.
 *  - The controls sit in one bar at the bottom centre, clear of the HUD's pause button and score at the top
 *    (UX-062), and fade while the mouse rests.
 */

import { useEffect, useRef, useState } from 'react';
import type { AppServices } from '../../app/registry/types';
import { useT } from '../../i18n/useT';
import { Button } from '../../ui/Button';
import { IconButton } from '../../ui/IconButton';
import { Kbd } from '../../ui/Kbd';
import { runCommand } from './hooks';
import { registerServices } from './live';
import { m } from './preview.messages';
import { previewStageOf } from './previewLoop';
import { revealAtSec } from './testPlayState';
import s from './Preview.module.css';

/** Unmodified keys that keep their app bindings in Theater whatever the preset (seeking, rate, Theater, test play). */
export const THEATER_KEYS: ReadonlySet<string> = new Set([
  'Space',
  'Backslash',
  'F5',
  'Home',
  'End',
  'PageUp',
  'PageDown',
  'ArrowUp',
  'ArrowDown',
  'KeyT',
  'KeyU',
  'KeyN',
  'Comma',
  'Period',
]);

/**
 * Whether a key pressed in Theater reaches the app's bindings: the fixed `THEATER_KEYS`, or a chord (or hold chord)
 * that the active keymap binds to a Playback-category command, so a preset's own playback keys keep working.
 */
export function theaterKeyPasses(services: Pick<AppServices, 'keymap' | 'registry'>, e: KeyboardEvent): boolean {
  if (THEATER_KEYS.has(e.code)) return true;
  const chord = services.keymap.chordOf(e);
  const hold = `hold:${e.code}`;
  for (const b of services.keymap.bindings()) {
    if (b.chord !== chord && b.chord !== hold) continue;
    if (services.registry.command(b.command)?.category === 'Playback') return true;
  }
  return false;
}

/** Controls fade after this long without mouse movement (ms). */
export const IDLE_MS = 2500;

/**
 * Focuses `root` while mounted and gives focus back to what had it before. While mounted, an editor region under the
 * full-window overlay (`[data-region]`: the lane, the motion columns...) never keeps the focus: a command that
 * focuses one (a playback command revealing the lane) would otherwise take the overlay's keys (Esc, the playback
 * keys) to the hidden editor. Dialogs, the palette and menus (portals outside any region) keep their focus.
 */
export function useFocusWhileMounted(root: React.RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const prev = document.activeElement;
    root.current?.focus({ preventScroll: true });
    const onFocusIn = (e: FocusEvent): void => {
      const el = root.current;
      const target = e.target;
      if (el === null || !(target instanceof Element) || el.contains(target)) return;
      if (target.closest('[data-region]') !== null) el.focus({ preventScroll: true });
    };
    document.addEventListener('focusin', onFocusIn);
    return () => {
      document.removeEventListener('focusin', onFocusIn);
      if (prev instanceof HTMLElement && prev.isConnected) prev.focus({ preventScroll: true });
    };
  }, [root]);
}

export function Theater(props: { readonly services: AppServices }) {
  const { services } = props;
  const t = useT();
  const rootRef = useRef<HTMLDivElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const [idle, setIdle] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    registerServices(services);
    const host = hostRef.current;
    if (host === null) return undefined;
    return previewStageOf(services).attach(host, 'theater');
  }, [services]);

  useFocusWhileMounted(rootRef);

  useEffect(() => {
    timer.current = setTimeout(() => {
      setIdle(true);
    }, IDLE_MS);
    return () => {
      if (timer.current !== null) clearTimeout(timer.current);
    };
  }, []);

  const wake = (): void => {
    setIdle(false);
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setIdle(true);
    }, IDLE_MS);
  };

  const exit = (): void => {
    services.editor.getState().setPreview({ theater: false });
  };

  /** Esc: stop playback where it started (RPE NAV-07), then back to the editor. */
  const stopAndExit = (): void => {
    const transport = services.audio.transport;
    if (transport.state.playing) transport.stop({ returnToStart: true });
    // UX-055: the transport is back where the play started; take the lane and motion columns there too.
    revealAtSec(services, transport.chartTime());
    exit();
  };

  return (
    <div
      ref={rootRef}
      className={s.fullWindow}
      role="region"
      aria-label={t(m.theaterLabel)}
      tabIndex={-1}
      data-keys="native"
      data-theater=""
      onPointerMove={wake}
      onKeyDown={(e) => {
        if (e.ctrlKey || e.altKey || e.metaKey) return;
        if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          stopAndExit();
          return;
        }
        // Playback keys reach the app's bindings; editing keys do nothing while the chart plays full-window.
        if (!theaterKeyPasses(services, e.nativeEvent)) e.stopPropagation();
      }}
    >
      <div ref={hostRef} className={s.fullStage} />
      <div
        className={s.fullBar}
        role="group"
        aria-label={t(m.theaterControls)}
        data-idle={idle ? 'true' : 'false'}
        data-theater-bar=""
      >
        <span className={s.fullBarHint} aria-hidden={idle}>
          <Kbd chord="Space" /> {t(m.theaterHintPlay)} <Kbd chord="Escape" /> {t(m.theaterHintStop)}{' '}
          <Kbd chord="Backslash" /> {t(m.theaterHintExit)}
        </span>
        <Button
          size="sm"
          icon="Gamepad2"
          command="preview.testPlay"
          onClick={() => {
            runCommand(services, 'preview.testPlay', { from: 'playhead' });
          }}
        >
          {t(m.cmdTestPlay)}
        </Button>
        <IconButton icon="Minimize" label={{ key: m.theaterExit }} command="preview.theater" onClick={exit} />
      </div>
    </div>
  );
}
